<template>
  <div class="service-info">
    <div class="layout">
      <div class="scroll-progress-bar" :style="{ width: progress + '%' }"></div>

      <div class="button-wrapper-area" :class="{ hide: !isVisible }">
        <div class="button-wrapper">
          <div class="button-group">
            <span class="group-label">공통</span>
            <button
                v-for="section in commonSections"
                :key="section.id"
                :class="{ active: activeIndex === section.id }"
                @click="scrollToSection(section.id)"
            >
              {{ section.label }}
            </button>
          </div>

          <div class="button-group">
            <span class="group-label">아티스트 회원</span>
            <button
                v-for="section in artistSections"
                :key="section.id"
                :class="{ active: activeIndex === section.id }"
                @click="scrollToSection(section.id)"
            >
              {{ section.label }}
            </button>
          </div>
        </div>
      </div>


      <div class="section-wrapper" @scroll.passive="onScroll" ref="scrollContainer">
        <div
            v-for="section in sections"
            :key="section.id"
            class="image-section"
            :data-id="section.id"
        >
          <img :src="section.src" alt="" loading="lazy"/>
        </div>
      </div>
    </div>
  </div>

</template>


<script setup>
import {onMounted, onBeforeUnmount, computed, ref, nextTick, watch} from 'vue'
import {useDeviceStore} from "@/store/deviceStore";
const progress = ref(0);
const deviceStore = useDeviceStore();
const isMobile = computed(() => deviceStore.isMobile);
const sectionLabels = [
  {id: 1, label: '플랫폼 소개'},
  {id: 2, label: '회원별 주요 서비스'},
  {id: 3, label: '플랫폼 한눈에 보기'},
  {id: 4, label: '회원 가입 및 승인'},
  {id: 5, label: '프로필 등록'},
  {id: 6, label: '아트워크 등록 및 관리'},
  {id: 7, label: '포트폴리오 제작'},
  {id: 8, label: '공모 지원하기'},
  {id: 9, label: 'Q&A'},
  {id: 10, label: 'CONTACT US'},
]

const sections = computed(() => {
  const cdnPublic = process.env.VUE_APP_CDN_PUBLIC || 'https://public.mimio.life';
  const basePath = cdnPublic + '/info/service/' + (isMobile.value ? 'mo' : 'pc');
  return sectionLabels.map(s => ({
    ...s,
    src: `${basePath}/${s.id}.png`
  }));
})

const commonSections = computed(() => sections.value.filter(s => [1, 2, 3, 9, 10].includes(s.id)))
const artistSections = computed(() => sections.value.filter(s => [4, 5, 6, 7, 8].includes(s.id)))

const activeIndex = ref(1)
const sectionRefs = []
const scrollContainer = ref(null)

let scrollTimeout = null
let isProgrammaticScroll = false

const scrollToSection = (id) => {
  if (isProgrammaticScroll) {
    // 이미 스크롤 중이면 초기화 후 새로운 스크롤 동작 시작
    clearTimeout(scrollTimeout);
  }
  isProgrammaticScroll = true
  activeIndex.value = id

  const el = sectionRefs.value.find(item => item.dataset.id == id)
  if (el) {
    window.scrollTo({
      top: el.offsetTop,
      behavior: 'smooth'
    })

    scrollTimeout = setTimeout(() => {
      updateProgress();
      isProgrammaticScroll = false;
    }, 1000) // 스크롤 지속 시간에 따라 조정
  }
}

let debounceTimeout = null;
const onScroll = () => {
  if (isProgrammaticScroll) {
    return;
  }

  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }

  updateProgress();

  debounceTimeout = setTimeout(() => {
    const scrollOffset = window.scrollY + 70;
    const refs = sectionRefs.value;
    const lastSection = refs[refs.length - 1];

    // 스크롤이 끝까지 오면 마지막 섹션으로 activeIndex 설정
    //console.log(scrollOffset, window.innerHeight, lastSection.offsetTop, lastSection.offsetHeight)
    if (lastSection && scrollOffset + window.innerHeight >= lastSection.offsetTop + lastSection.offsetHeight) {
      activeIndex.value = parseInt(lastSection.dataset.id);
      return;
    }

    for (let i = 0; i < refs.length; i++) {
      const el = refs[i];
      const nextOffsetTop = refs[i + 1]?.offsetTop ?? Infinity;
      if (scrollOffset >= el.offsetTop && scrollOffset < nextOffsetTop) {
        activeIndex.value = parseInt(el.dataset.id);
        break;
      }
    }

  }, 100);

}

watch(activeIndex, () => {
  scrollActiveButtonIntoView()
})

const scrollActiveButtonIntoView = () => {
  nextTick(() => {
    const activeBtn = document.querySelector('.button-wrapper-area .active')
    if (activeBtn) {
      activeBtn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
    }
  })
}

const updateProgress = () => {
  const scrollTop = window.scrollY
  const docHeight = document.documentElement.scrollHeight - window.innerHeight
  progress.value = Math.min((scrollTop / docHeight) * 100, 100);

  //handleScrollDirection();
}

const isVisible = ref(true)
let lastScrollY = window.scrollY

const handleScrollDirection = () => {
  const currentY = window.scrollY
  isVisible.value = currentY < lastScrollY || currentY < 100
  lastScrollY = currentY
}

onMounted(() => {
  sectionRefs.value = Array.from(document.querySelectorAll('.image-section'))
  window.addEventListener('scroll', onScroll)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
})

</script>


<style scoped>
.scroll-progress-bar {
  position: fixed;
  top: 66px;
  left: 0;
  height: 4px;
  background: #E2A800;
  z-index: 10;
  transition: width 0.1s ease-out;
}


.service-info {
  background-color: white;
}

.button-wrapper-area {
  position: fixed;
  background: #FFF5E8;
  top: 66px;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.button-wrapper-area {
  transition: transform 0.3s ease;
}

.button-wrapper-area.hide {
  transform: translateY(-100%);
}

.button-wrapper {
  position: sticky;
  height: 66px;
  display: flex;
  justify-content: space-evenly;
  flex-direction: row;
  align-items: center;
  max-width: 1620px;
  width: 100%;
}

.button-wrapper .button-group {
  display: flex;
  flex-direction: row;
  column-gap: 20px;
  align-items: center;

}

.button-wrapper .group-label {
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  text-align: center;
  letter-spacing: -0.03em;
  text-transform: capitalize;
  color: #151419;
}

.button-wrapper button {
  border: none;
  background: transparent;
  cursor: pointer;
  font-weight: bold;
  border-bottom: 2px solid transparent;
  transition: all 0.3s;

  font-weight: 500;
  font-size: 14px;
  text-align: center;
  color: #151419;
}


/*
.button-wrapper button.active {
  color: royalblue;
  border-bottom: 2px solid royalblue;
  font-weight: 600;
}
 */

.button-wrapper button.active {
  background: #151419;
  border: 1px solid #151419;
  border-radius: 5px;

  font-weight: 600;

  color: #FFFFFF;
  padding: 11px 13px;
}



.section-wrapper {
  margin-top: 66px;
}

.image-section {
  width: 100%;
}

.image-section img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

@media (max-width: 1300px) {
  .button-wrapper {
    flex-direction: column;
    row-gap: 10px;
    height: 80px;
  }

  .section-wrapper {
    margin-top: 80px;
  }

  .button-wrapper .group-label {
    font-size: 14px;
  }

  .button-wrapper button {
    font-size: 12px;
  }

  .button-wrapper button.active {
    padding: 6px 5.4px;
  }
}

@media (max-width: 570px) {
  .button-wrapper {
    align-items: flex-start;
    padding: 0 10px;
  }

  .button-wrapper .button-group {
    overflow-x: auto;
    white-space: nowrap;
    -ms-overflow-style: none; /* IE */
    scrollbar-width: none; /* Firefox */
    width: 100%;
    padding-right: 30px;
  }
}

@media (max-width: 460px) {
  .button-wrapper-area {
    top: 41px;
  }
  .scroll-progress-bar{
    top: 41px;
  }

}
</style>
